import React, { memo } from 'react';
import { usePreferences } from '../../contexts/PreferencesContext';
import { SearchNavigationState } from '../../hooks/useSearchNavigation';
import { TranscriptUtterance, Speaker } from '../../types';
import EditorUtteranceMeta from './EditorUtteranceMeta';
import EditorUtteranceContent from './EditorUtteranceContent';

interface EditorUtteranceProps {
  utterance: TranscriptUtterance;
  speakers: Speaker[];
  needsDOMSync: boolean;
  isCurrentUtterance: boolean;
  currentWordIndex: number;
  isPlaying: boolean;
  searchState: SearchNavigationState;
  onSpeakerChange: (utteranceId: string, speakerId: string) => void;
  onUtteranceClick: (e: React.MouseEvent, utteranceId: string) => void;
  onKeyDown: (e: React.KeyboardEvent, utteranceId: string) => void;
  onInput: (e: React.FormEvent<HTMLDivElement>, utteranceId: string) => void;
  onPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
}

const EditorUtterance: React.FC<EditorUtteranceProps> = ({
  utterance,
  speakers,
  needsDOMSync,
  isCurrentUtterance,
  currentWordIndex,
  isPlaying,
  searchState,
  onSpeakerChange,
  onUtteranceClick,
  onKeyDown,
  onInput,
  onPaste,
}) => {
  const { preferences } = usePreferences();
  const isColumnView = preferences.transcript?.utterance_meta_column ?? false;

  return (
    <div
      data-utterance-id={utterance.id}
      className={`border rounded-lg shadow-sm group transition-colors duration-200 relative ${
        isCurrentUtterance
          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-600 shadow-md'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
      }`}
    >
      {isColumnView ? (
        // Column layout (original)
        <div className="flex">
          <div className="w-48 flex-shrink-0 p-3 bg-gray-50 dark:bg-gray-700 border-r border-gray-200 dark:border-gray-600 space-y-2 rounded-l-lg">
            <EditorUtteranceMeta
              utterance={utterance}
              speakers={speakers}
              onSpeakerChange={onSpeakerChange}
              onUtteranceClick={onUtteranceClick}
            />
          </div>

          <div className="flex-1 min-w-0">
            <EditorUtteranceContent
              utterance={utterance}
              needsDOMSync={needsDOMSync}
              isCurrentUtterance={isCurrentUtterance}
              currentWordIndex={currentWordIndex}
              isPlaying={isPlaying}
              searchState={searchState}
              onUtteranceClick={onUtteranceClick}
              onKeyDown={onKeyDown}
              onInput={onInput}
              onPaste={onPaste}
            />
          </div>
        </div>
      ) : (
        // Panel layout (new default)
        <div>
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 rounded-t-lg">
            <EditorUtteranceMeta
              utterance={utterance}
              speakers={speakers}
              onSpeakerChange={onSpeakerChange}
              onUtteranceClick={onUtteranceClick}
              horizontal={true}
            />
          </div>
          <EditorUtteranceContent
            utterance={utterance}
            needsDOMSync={needsDOMSync}
            isCurrentUtterance={isCurrentUtterance}
            currentWordIndex={currentWordIndex}
            isPlaying={isPlaying}
            searchState={searchState}
            onUtteranceClick={onUtteranceClick}
            onKeyDown={onKeyDown}
            onInput={onInput}
            onPaste={onPaste}
          />
        </div>
      )}
    </div>
  );
};

export default memo(EditorUtterance);
